/**
 * /withdraw — 提现申请 + 历史记录
 *
 * 设计稿: _design_reference/mini-withdraw.html
 *
 * 功能分两个视图(header 右侧按钮切换):
 *   - "form" view: 余额 hero → 快选金额 → 自定义输入 → 提现方式 → 确认摘要 → 提交
 *   - "history" view: 历史提现记录列表
 *
 * API 说明:
 *   - useUserInfo: 取 amountCny(钱包可提 CNY) + commissionBalanceCny(佣金可提 CNY)
 *   - useApplyWithdraw: POST /balance/withdraw/apply — amount 为 CNY 浮点数
 *   - useWithdrawList: GET /balance/withdraw/list — 历史记录
 *
 * 提现金额单位: API 接受 CNY (¥). 设计稿 hero 展示 CNY,快选按钮为 CNY 预设值。
 * userCardId: 留空("")，后端对 type=amount 默认走注册绑定账户 (同 web 行为)。
 */

import {
  useApplyWithdraw,
  useUserInfo,
  useWithdrawList,
  type WithdrawType,
} from "@kupikupi/api";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TgEmpty } from "../components/TgEmpty";
import { TgHeader } from "../components/TgHeader";
import { TgMain } from "../components/TgMain";
import { TgPage } from "../components/TgPage";
import { TgSkeleton } from "../components/TgSkeleton";
import { useAuth, useAuthToken } from "../lib/auth";

// ─── Validation ─────────────────────────────────────────────────────────────

const WITHDRAW_MIN = 10;
const WITHDRAW_MAX = 20000;

type ValidationReason = "empty" | "too_small" | "too_large" | "over_balance";
type ValidationResult = { ok: true; amount: number } | { ok: false; reason: ValidationReason };

function validateWithdrawAmount(
  raw: string,
  maxBalance: number | null | undefined,
): ValidationResult {
  const cleaned = raw.replace(/[^0-9.]/g, "");
  if (cleaned === "") return { ok: false, reason: "empty" };
  const amount = Number.parseFloat(cleaned);
  if (!Number.isFinite(amount) || amount <= 0) return { ok: false, reason: "empty" };
  if (amount < WITHDRAW_MIN) return { ok: false, reason: "too_small" };
  if (amount > WITHDRAW_MAX) return { ok: false, reason: "too_large" };
  if (typeof maxBalance === "number" && maxBalance >= 0 && amount > maxBalance) {
    return { ok: false, reason: "over_balance" };
  }
  return { ok: true, amount };
}

// ─── CNY formatter ───────────────────────────────────────────────────────────

function formatCny(val: number | null | undefined): string {
  if (val == null) return "—";
  return new Intl.NumberFormat("zh-CN", {
    style: "decimal",
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(val);
}

// ─── Status badge ────────────────────────────────────────────────────────────

const STATUS_COLOR: Record<string, string> = {
  audit: "var(--color-text-muted)",
  processing: "var(--color-primary-fill)",
  success: "#1aa37a",
  refusal: "var(--color-danger, #d4543b)",
  failed: "var(--color-danger, #d4543b)",
};

// ─── Quick-select presets (CNY) ───────────────────────────────────────────────

const PRESETS_CNY = [100, 500, 1000, 3000] as const;

// ─── Main page ───────────────────────────────────────────────────────────────

type View = "form" | "history";

export function WithdrawPage() {
  const { t } = useTranslation("withdraw");
  const { status } = useAuth();
  const authToken = useAuthToken();

  const [view, setView] = useState<View>("form");

  // ─ data hooks ─
  const userQ = useUserInfo(authToken);
  const withdrawListQ = useWithdrawList(authToken, { pageNo: 1, pageSize: 30 });
  const applyMut = useApplyWithdraw(authToken);

  // ─ form state ─
  const [type, setType] = useState<WithdrawType>("amount");
  const [rawAmount, setRawAmount] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // ─ derived balance (CNY) ─
  const userInfo = userQ.data;
  const walletCny = userInfo?.amountCny ?? null;
  const commissionCny = userInfo?.commissionBalanceCny ?? null;
  const balance = type === "amount" ? walletCny : commissionCny;

  // Parsed amount for summary card
  const parsedAmount = (() => {
    const cleaned = rawAmount.replace(/[^0-9.]/g, "");
    const n = Number.parseFloat(cleaned);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();

  const busy = applyMut.isPending;

  // ─ validation error message ─
  function errorFor(reason: ValidationReason): string {
    switch (reason) {
      case "too_small":
        return t("form.error_too_small", { min: WITHDRAW_MIN });
      case "too_large":
        return t("form.error_too_large", { max: WITHDRAW_MAX });
      case "over_balance":
        return t("form.error_over_balance");
      default:
        return t("form.error_amount");
    }
  }

  // ─ handlers ─
  function handlePreset(cny: number) {
    setRawAmount(String(cny));
    setError(null);
    setSuccessMsg(null);
  }

  function handleMax() {
    if (balance != null && balance > 0) {
      setRawAmount(String(Math.min(balance, WITHDRAW_MAX)));
      setError(null);
      setSuccessMsg(null);
    }
  }

  async function handleSubmit() {
    if (busy) return;
    setSuccessMsg(null);
    const result = validateWithdrawAmount(rawAmount, balance);
    if (!result.ok) {
      setError(errorFor(result.reason));
      return;
    }
    setError(null);
    try {
      await applyMut.mutateAsync({ type, amount: result.amount, userCardId: "" });
      setRawAmount("");
      setSuccessMsg(t("form.success"));
      // Refresh history in background
      withdrawListQ.refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("form.error_failed"));
    }
  }

  // ─ auth guard ─
  if (status === "booting") {
    return (
      <TgPage tabbar={false}>
        <TgHeader title={t("head.title")} back />
        <div className="tg-body" style={{ display: "flex", flexDirection: "column", gap: 10, padding: "16px 0" }}>
          {[80, 14, 14, 14, 14].map((h, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows
            <TgSkeleton key={i} height={h} />
          ))}
        </div>
      </TgPage>
    );
  }

  if (status === "signed-out") {
    return (
      <TgPage tabbar={false}>
        <TgHeader title={t("head.title")} back />
        <div
          style={{
            padding: "60px 24px",
            textAlign: "center",
            color: "var(--color-text-muted)",
            fontSize: 13,
          }}
        >
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("state.signin_t")}</div>
          <div>{t("state.signin_s")}</div>
        </div>
      </TgPage>
    );
  }

  // ─ header right button ─
  const headerRight = (
    <button
      type="button"
      className="tg-header__r"
      onClick={() => setView((v) => (v === "form" ? "history" : "form"))}
      style={{ fontSize: 12 }}
    >
      {view === "form" ? t("mini.records_btn") : t("mini.view_form")}
    </button>
  );

  // ─ history view ─
  if (view === "history") {
    const records = withdrawListQ.data?.records ?? [];
    const historyLoading = withdrawListQ.isLoading;

    return (
      <TgPage tabbar={false}>
        <TgHeader title={t("history.title")} back right={headerRight} />
        <div className="tg-body" style={{ flex: 1 }}>
          {historyLoading && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "16px 0" }}>
              {[14, 14, 14, 14, 14].map((h, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows
                <TgSkeleton key={i} height={h} />
              ))}
            </div>
          )}
          {!historyLoading && records.length === 0 && (
            <TgEmpty icon="💸" title={t("empty.title")} hint={t("empty.hint")} />
          )}
          {records.map((w) => {
            const statusColor = STATUS_COLOR[w.status] ?? "var(--color-text-muted)";
            return (
              <div key={w.id} className="tg-card">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {w.payeeName || t("type." + w.type)}
                      {w.payeeBank ? ` · ${w.payeeBank}` : ""}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: "var(--color-text-muted)",
                        fontFamily: "var(--font-mono)",
                        marginTop: 3,
                      }}
                    >
                      {w.createTime}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", marginLeft: 12, flexShrink: 0 }}>
                    <div
                      style={{
                        fontFamily: "var(--font-display)",
                        fontWeight: 900,
                        fontSize: 16,
                      }}
                    >
                      ¥{formatCny(w.amountRubKopecks / 100)}
                    </div>
                    <div style={{ fontSize: 10, color: statusColor, marginTop: 2 }}>
                      {t("status." + w.status, { defaultValue: w.status })}
                    </div>
                  </div>
                </div>
                {w.message && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--color-text-muted)",
                      marginTop: 6,
                    }}
                  >
                    {w.message}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </TgPage>
    );
  }

  // ─ form view ─────────────────────────────────────────────────────────────

  const heroLoading = userQ.isLoading;

  return (
    <TgPage tabbar={false}>
      <TgHeader title={t("head.title")} back borderless right={headerRight} />

      {/* Hero gradient banner */}
      <div
        style={{
          background: "linear-gradient(135deg,var(--color-primary),var(--color-primary-fill))",
          color: "#fff",
          padding: "20px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontFamily: "monospace",
            fontSize: 10,
            opacity: 0.75,
            letterSpacing: "0.06em",
          }}
        >
          {t("mini.hero_label")}
        </div>
        {heroLoading ? (
          <div
            style={{
              height: 44,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              opacity: 0.7,
            }}
          >
            …
          </div>
        ) : (
          <div
            style={{
              fontFamily: "-apple-system,system-ui,sans-serif",
              fontSize: 36,
              fontWeight: 900,
              marginTop: 4,
              letterSpacing: "-0.02em",
            }}
          >
            {formatCny(balance)}
          </div>
        )}
        {commissionCny != null && commissionCny > 0 && (
          <div style={{ fontSize: 10, opacity: 0.7, marginTop: 4 }}>
            {t("mini.hero_bonus_note", { amount: formatCny(commissionCny) })}
          </div>
        )}
      </div>

      <div className="tg-body" style={{ flex: 1 }}>

        {/* Amount section */}
        <div className="tg-card">
          <h4 className="tg-card__h">{t("mini.amount_section")}</h4>

          {/* Type toggle: wallet vs commission */}
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            {(["amount", "commission"] as const).map((opt) => {
              const active = type === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setType(opt);
                    setRawAmount("");
                    setError(null);
                    setSuccessMsg(null);
                  }}
                  style={{
                    flex: 1,
                    background: active
                      ? "var(--color-primary-fill)"
                      : "#fff",
                    color: active ? "#fff" : "var(--color-text)",
                    border: `1.5px solid ${active ? "var(--color-primary-fill)" : "var(--color-border)"}`,
                    borderRadius: 8,
                    padding: "7px 0",
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {opt === "amount" ? t("mini.method_wallet") : t("mini.method_commission")}
                </button>
              );
            })}
          </div>

          {/* Quick-select preset buttons */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2,1fr)",
              gap: 6,
              marginBottom: 8,
            }}
          >
            {PRESETS_CNY.map((preset) => {
              const isSelected = rawAmount === String(preset);
              return (
                <button
                  key={preset}
                  type="button"
                  disabled={busy}
                  onClick={() => handlePreset(preset)}
                  style={{
                    background: isSelected
                      ? "linear-gradient(180deg,var(--color-primary-soft),#fff)"
                      : "#fff",
                    border: `1.5px solid ${isSelected ? "var(--color-primary-fill)" : "var(--color-border)"}`,
                    borderRadius: 10,
                    padding: 10,
                    fontFamily: "-apple-system,system-ui,sans-serif",
                    fontSize: 13,
                    fontWeight: 900,
                    color: isSelected ? "var(--color-primary-fill)" : "var(--color-text)",
                    cursor: "pointer",
                  }}
                >
                  ¥{preset}
                </button>
              );
            })}
            {/* "All" preset button */}
            {balance != null && balance > 0 && (
              <button
                type="button"
                disabled={busy}
                onClick={handleMax}
                style={{
                  background:
                    rawAmount === String(Math.min(balance, WITHDRAW_MAX))
                      ? "linear-gradient(180deg,var(--color-primary-soft),#fff)"
                      : "#fff",
                  border: `1.5px solid ${
                    rawAmount === String(Math.min(balance, WITHDRAW_MAX))
                      ? "var(--color-primary-fill)"
                      : "var(--color-border)"
                  }`,
                  borderRadius: 10,
                  padding: 10,
                  fontFamily: "-apple-system,system-ui,sans-serif",
                  fontSize: 13,
                  fontWeight: 900,
                  color:
                    rawAmount === String(Math.min(balance, WITHDRAW_MAX))
                      ? "var(--color-primary-fill)"
                      : "var(--color-text)",
                  cursor: "pointer",
                }}
              >
                {t("mini.preset_all", { amount: formatCny(Math.min(balance, WITHDRAW_MAX)) })}
              </button>
            )}
          </div>

          {/* Custom input */}
          <div className="e-input-wrap">
            <span className="e-input-prefix">¥</span>
            <input
              className="e-input e-input--with-prefix"
              placeholder={t("mini.custom_placeholder")}
              value={rawAmount}
              inputMode="decimal"
              maxLength={10}
              disabled={busy}
              style={{ height: 38, fontSize: 12 }}
              onChange={(e) => {
                setRawAmount(e.target.value);
                setError(null);
                setSuccessMsg(null);
              }}
            />
          </div>

          {/* Validation / success messages */}
          {error && (
            <p
              style={{
                color: "var(--color-danger, #d4543b)",
                fontSize: 12,
                marginTop: 6,
                marginBottom: 0,
              }}
            >
              {error}
            </p>
          )}
          {successMsg && (
            <p
              style={{
                color: "#1aa37a",
                fontSize: 12,
                marginTop: 6,
                marginBottom: 0,
              }}
            >
              {successMsg}
            </p>
          )}
        </div>

        {/* Summary dark card (shown only when amount is valid) */}
        {parsedAmount != null && (
          <div
            style={{
              background: "linear-gradient(135deg,var(--color-text),#1A2924)",
              color: "#fff",
              padding: "16px 18px",
              borderRadius: 12,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
              margin: "0 0 8px",
            }}
          >
            <div>
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: 9,
                  opacity: 0.6,
                }}
              >
                {t("mini.summary_label")}
              </div>
              <div
                style={{
                  fontFamily: "-apple-system,system-ui,sans-serif",
                  fontSize: 22,
                  fontWeight: 900,
                  color: "var(--color-sticker-yellow, #F5D25A)",
                }}
              >
                ¥{formatCny(parsedAmount)}
              </div>
            </div>
            <div style={{ fontSize: 10, opacity: 0.7, textAlign: "right" }}>
              {t("mini.summary_days")}
              <br />
              {type === "amount" ? t("mini.summary_source_amount") : t("mini.summary_source_commission")}
            </div>
          </div>
        )}

      </div>

      {/* Sticky CTA */}
      <TgMain
        disabled={busy || parsedAmount == null}
        onClick={handleSubmit}
      >
        {busy ? t("mini.cta_pending") : t("mini.cta")}
      </TgMain>
    </TgPage>
  );
}
